import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import constant from '../../constant';
import { v4_chat_message_fetch, v4_chat_message_send } from '../../actions/v4/mailBox';
import { useMessageContext } from '../../context/MessageContext';
import { getSessionStorage } from '../../utils';
import { formatLocalTime } from '../../dateTimeFormat';
import { ChatAttachmentPreviewList } from './chatAttachmentPreview';

const CHAT_HISTORY_LIMIT = 100;
const WINDOW_WIDTH = 320;
const WINDOW_GAP = 12;

const getCurrentUserFromSession = () => {
  try {
    const encoded = getSessionStorage(constant.keys.userDetails);
    if (!encoded) return { id: null, fullName: '' };
    const parsed = JSON.parse(atob(encoded));
    const id = Number(parsed?.id);
    return {
      id: Number.isFinite(id) ? id : null,
      fullName: parsed?.fullName || '',
    };
  } catch (error) {
    return { id: null, fullName: '' };
  }
};

const getMessageTime = (value) => {
  return formatLocalTime(value);
};

const FloatingChatWindow = ({ chat, index }) => {
  const {
    newMessages,
    getUnreadCountForUser,
    clearUnreadForUser,
    closeFloatingChatWindow,
    toggleFloatingChatMinimize,
    setFloatingChatMinimized,
    openFloatingChatWindow,
  } = useMessageContext();

  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const currentUser = useMemo(() => getCurrentUserFromSession(), []);
  const messageBodyRef = useRef(null);
  const unreadCount = getUnreadCountForUser(chat.userId);

  useEffect(() => {
    if (!chat?.userId) return;

    let active = true;
    v4_chat_message_fetch(chat.userId, CHAT_HISTORY_LIMIT).then((res) => {
      if (!active) return;
      if (res?.status && Array.isArray(res.result)) {
        setMessages(res.result);
      } else {
        setMessages([]);
      }
    }).catch(() => {
      if (active) {
        setMessages([]);
      }
    });

    return () => {
      active = false;
    };
  }, [chat?.userId]);

  useEffect(() => {
    if (!chat?.userId || !Array.isArray(newMessages) || newMessages.length === 0) return;

    const targetUserId = Number(chat.userId);
    setMessages((prev) => {
      const existingIds = new Set(prev.map((item) => String(item?.id)));
      const incoming = newMessages
        .filter((item) => Number(item?.from) === targetUserId || Number(item?.to) === targetUserId)
        .map((item) => item?.messageData)
        .filter((item) => item?.id && !existingIds.has(String(item.id)));

      if (!incoming.length) return prev;
      return [...prev, ...incoming].slice(-CHAT_HISTORY_LIMIT);
    });
  }, [chat?.userId, newMessages]);

  useEffect(() => {
    if (chat?.minimized) return;
    clearUnreadForUser(chat.userId);
  }, [chat?.minimized, chat?.userId, clearUnreadForUser]);

  useEffect(() => {
    if (chat?.minimized || !messageBodyRef.current) return;
    window.requestAnimationFrame(() => {
      if (!messageBodyRef.current) return;
      messageBodyRef.current.scrollTop = messageBodyRef.current.scrollHeight;
    });
  }, [messages, chat?.minimized]);

  const handleSend = async () => {
    const trimmed = (messageText || '').trim();
    if (!trimmed || sending) return;

    setSending(true);
    try {
      const formData = new FormData();
      formData.append('message', trimmed);
      formData.append('tagged_message', '');

      const res = await v4_chat_message_send(chat.userId, formData);
      if (!res?.status) {
        toast.error(res?.error || 'Failed to send message');
        return;
      }

      const localMessage = {
        id: res?.result?.id || `local-${Date.now()}`,
        text: trimmed,
        send_on: new Date().toISOString(),
        from_user: currentUser.fullName || 'You',
        to_user: chat.fullName || '',
        type: 'sent',
      };

      setMessages((prev) => [...prev, localMessage].slice(-CHAT_HISTORY_LIMIT));
      setMessageText('');
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const rightOffset = 16 + (index * (WINDOW_WIDTH + WINDOW_GAP));

  return (
    <div
      className={`floating-chat-window ${chat?.minimized ? 'is-minimized' : ''}`}
      style={{ right: `${rightOffset}px` }}
      onMouseDown={() => openFloatingChatWindow({ userId: chat.userId, fullName: chat.fullName, minimized: chat.minimized })}
    >
      <div
        className="floating-chat-header"
        onClick={() => {
          if (chat?.minimized) {
            setFloatingChatMinimized(chat.userId, false);
          }
        }}
      >
        <div className="floating-chat-header-title">
          {chat.fullName || `User ${chat.userId}`}
          {chat?.minimized && unreadCount > 0 && (
            <span className="badge rounded-pill bg-danger ms-2">{unreadCount > 9 ? '9+' : unreadCount}</span>
          )}
        </div>
        <div className="floating-chat-actions">
          <button
            type="button"
            className="floating-chat-btn"
            onClick={(event) => {
              event.stopPropagation();
              toggleFloatingChatMinimize(chat.userId);
            }}
            title={chat?.minimized ? 'Expand' : 'Minimize'}
          >
            {chat?.minimized ? '▢' : '—'}
          </button>
          <button
            type="button"
            className="floating-chat-btn"
            onClick={(event) => {
              event.stopPropagation();
              closeFloatingChatWindow(chat.userId);
            }}
            title="Close"
          >
            ×
          </button>
        </div>
      </div>

      {!chat?.minimized && (
        <>
          <div className="floating-chat-body" ref={messageBodyRef}>
            {messages.length === 0 && (
              <div className="floating-chat-empty">No messages yet</div>
            )}
            {messages.map((message) => {
              const messageId = String(message?.id || `${message?.send_on}-${message?.text || ''}`);
              const messageType = String(message?.type || '').toLowerCase();
              const isMine = messageType === 'sent' || Number(message?.from_user_id) === currentUser.id;

              return (
                <div key={messageId} className={`floating-chat-item ${isMine ? 'me' : 'them'}`}>
                  <div className="floating-chat-bubble">
                    {message?.text ? <div className="floating-chat-text">{message.text}</div> : null}
                    <ChatAttachmentPreviewList message={message} compact />
                  </div>
                  <div className="floating-chat-time">{getMessageTime(message?.send_on)}</div>
                </div>
              );
            })}
          </div>

          <div className="floating-chat-input-row">
            <input
              type="text"
              className="form-control form-control-sm floating-chat-input"
              placeholder="Type a message..."
              value={messageText}
              onChange={(event) => setMessageText(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  handleSend();
                }
              }}
            />
            <button
              type="button"
              className="btn btn-sm floating-chat-send-btn"
              disabled={sending || !(messageText || '').trim()}
              onClick={handleSend}
            >
              Send
            </button>
          </div>
        </>
      )}
    </div>
  );
};

const FloatingChatDock = () => {
  const { floatingChatWindows } = useMessageContext();

  if (!Array.isArray(floatingChatWindows) || floatingChatWindows.length === 0) {
    return null;
  }

  return (
    <>
      {floatingChatWindows.map((chat, index) => (
        <FloatingChatWindow key={`floating-chat-${chat.userId}`} chat={chat} index={index} />
      ))}
    </>
  );
};

export default FloatingChatDock;
